import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Divider, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import {
    CurrencyField,
    DatePicker,
    PaxPicker,
    SearchSelect,
    SearchSelectOption,
    Select,
    SelectCurrency,
    StaticDatePicker,
    SubmitButton,
    TextField,
    useForm,
} from '@travelity/form';
import { useFormState, useWatch } from 'react-hook-form';
import { addMonths, startOfToday } from 'date-fns';
import { ProductType, SourceType } from '@travelity/api';
import { useTranslation } from 'react-i18next';
import {
    FilterDateValue,
    FilterForm,
    FilterOption,
    FilterOptionValue,
    FilterPaxValue,
    FilterSourceValue,
    FilterTypes,
    FilterValue,
} from '../../filters.types';
import { SelectItemProduct } from '../../../select-item-product';
import SelectProduct from '../../../select-product/select-product';
import { directSources } from '../../../../views/availability/components/booking-side-form';

export interface FilterSettingsProps {
    filter: FilterOption;
    value?: FilterValue;
    onCancel?: () => void;
    onRemove?: () => void;
    selectedFilters: Record<string, FilterValue>;
    onUpdate: (v: any) => void;
}

const sourceTypes = [
    {
        value: SourceType.DIRECT,
        label: 'Direct',
    },
    {
        value: SourceType.REFERRAL,
        label: 'Referral',
    },
];

const today = startOfToday();

export const FilterSettings: React.FC<FilterSettingsProps> = props => {
    const { selectedFilters, filter, value, onCancel, onRemove, onUpdate } =
        props;

    const { Form, control, watch, reset, setValue } = useForm<FilterForm>({
        onSubmit: useCallback(
            (data: FilterForm) => {
                onUpdate(data.value);
            },
            [onUpdate]
        ),
        mode: 'onChange',
        defaultValues: {
            value,
        },
    });
    const formState = useFormState({ control });
    const { t } = useTranslation();

    const formValue = watch('value');
    const closeOnChange = filter.type === FilterTypes.DATE;
    useEffect(() => {
        if (closeOnChange && formState.isDirty) onUpdate(formValue);
    }, [formValue, closeOnChange, formState.isDirty]);

    const currentValue = useWatch({ name: 'value', control });
    const hasValue = useMemo(() => {
        if (filter.type === FilterTypes.DROPDOWN)
            return !!(currentValue as string[])?.length;
        if (filter.type === FilterTypes.SEARCH_PRODUCT)
            return !!(currentValue as FilterOptionValue)?.value;
        if (filter.type === FilterTypes.SOURCE)
            return !!(currentValue as FilterSourceValue)?.type;
        if (filter.type === FilterTypes.PAX)
            return !!Object.values(currentValue as FilterPaxValue).filter(
                v => v
            )?.length;
        return !!currentValue;
    }, [filter, currentValue]);

    useEffect(() => {
        if (
            filter.type === FilterTypes.DATES &&
            currentValue &&
            (currentValue as FilterDateValue).startDate &&
            (currentValue as FilterDateValue).endDate
        ) {
            const { startDate, endDate } = currentValue as FilterDateValue;
            if (startDate > endDate) {
                setValue('value.endDate', startDate);
            }
        }
    }, [filter, currentValue]);

    const disableBefore = filter.preventPast ? today : undefined;

    useEffect(() => {
        if (filter.type === FilterTypes.PAX) {
            reset({ value: watch('value') });
        }
    }, [filter]);

    return (
        <Form>
            <Stack sx={{ width: 370 }}>
                <Divider>{filter.label}</Divider>
                <Box sx={{ fontSize: '13px', mt: 1, color: '#949BAC' }}>
                    {filter.selectText}
                </Box>
                {filter.type === FilterTypes.PAX && <PaxPicker name="value" />}
                {filter.type === FilterTypes.DROPDOWN && (
                    <Stack direction="row" sx={{ mt: 2 }} gap="6px">
                        <SearchSelect
                            name="value"
                            label={filter.label}
                            multiple={filter.multiple}
                            options={filter.options as SearchSelectOption[]}
                            placeholder={`Select ${filter.label}`}
                            MenuItem={
                                filter.name === 'products'
                                    ? SelectItemProduct
                                    : undefined
                            }
                        />
                    </Stack>
                )}
                {filter.type === FilterTypes.SOURCE && (
                    <Stack sx={{ mt: 2 }} gap={2}>
                        <Select
                            name="value.type"
                            label={t('Source Type')}
                            placeholder={t('Select Source Type')}
                            options={sourceTypes}
                            fullWidth
                            MenuProps={{ sx: { zIndex: 10010 } }}
                        />
                        {watch('value.type') === SourceType.DIRECT ? (
                            <Select
                                name="value.name"
                                label={t('Source')}
                                placeholder={t('Select Source')}
                                options={directSources}
                                fullWidth
                                MenuProps={{ sx: { zIndex: 10010 } }}
                            />
                        ) : (
                            <TextField
                                name="value.referral"
                                label="Source"
                                placeholder="Type company, email or name"
                                fullWidth
                            />
                        )}
                    </Stack>
                )}
                {filter.type === FilterTypes.SEARCH_PRODUCT && (
                    <Stack direction="row" sx={{ mt: 2 }} gap="6px">
                        <SelectProduct
                            name="value"
                            types={
                                selectedFilters.productTypes as
                                    | ProductType[]
                                    | undefined
                            }
                            label={filter.label}
                            multiple={filter.multiple}
                            placeholder={`Select ${filter.label}`}
                        />
                    </Stack>
                )}
                {filter.type === FilterTypes.KEYWORD && (
                    <Stack direction="row" sx={{ mt: 2 }} gap="6px">
                        <TextField
                            name="value"
                            label=""
                            showErrors
                            placeholder={`Type ${filter.label}`}
                        />
                    </Stack>
                )}
                {filter.type === FilterTypes.DATES && (
                    <Stack direction="row" sx={{ mt: 2 }} gap="6px">
                        <DatePicker
                            name="value.startDate"
                            label=""
                            showErrors
                            TextFieldProps={{
                                helperText: '',
                                size: 'small',
                                placeholder: 'Start Date',
                            }}
                            disableBefore={disableBefore}
                        />
                        <DatePicker
                            name="value.endDate"
                            label=""
                            showErrors
                            TextFieldProps={{
                                helperText: '',
                                size: 'small',
                                placeholder: 'End Date',
                            }}
                            disableBefore={
                                (currentValue as FilterDateValue)?.startDate ||
                                disableBefore
                            }
                            disableAfter={
                                (currentValue as FilterDateValue)?.startDate
                                    ? addMonths(
                                          (currentValue as FilterDateValue)
                                              .startDate,
                                          filter.maxMonths || 2
                                      )
                                    : undefined
                            }
                        />
                    </Stack>
                )}
                {filter.type === FilterTypes.DATE && (
                    <StaticDatePicker name="value" label="" showErrors />
                )}
                {filter.type === FilterTypes.PRICE && (
                    <Stack direction="column" sx={{ mt: 2 }} gap="16px">
                        <SelectCurrency width="100%" name="value.currency" />
                        <CurrencyField
                            fullWidth
                            label="Min Price"
                            placeholder="Type Min Price"
                            name="value.minPrice"
                            currency={watch('value.currency') as string}
                        />
                        <CurrencyField
                            fullWidth
                            label="Max Price"
                            placeholder="Type Max Price"
                            name="value.maxPrice"
                            currency={watch('value.currency') as string}
                        />
                    </Stack>
                )}
                {filter.type === FilterTypes.TAGS && (
                    <Stack direction="row" sx={{ mt: 2 }} gap="6px">
                        <DatePicker
                            name="value.startDate"
                            label=""
                            showErrors
                            TextFieldProps={{
                                helperText: '',
                                size: 'small',
                                placeholder: 'Start Date',
                            }}
                        />
                        <DatePicker
                            name="value.endDate"
                            label=""
                            showErrors
                            TextFieldProps={{
                                helperText: '',
                                size: 'small',
                                placeholder: 'End Date',
                            }}
                        />
                    </Stack>
                )}

                {!closeOnChange && (
                    <Stack
                        direction="row"
                        gap={1}
                        sx={{ mt: 2 }}
                        alignItems="center"
                    >
                        {onCancel && (
                            <Button
                                variant="contained"
                                color="neutral"
                                sx={{ flexGrow: 1 }}
                                onClick={() => onCancel()}
                            >
                                Back
                            </Button>
                        )}
                        {onRemove && (
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    textAlign: 'center',
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        textDecoration: 'underline',
                                        cursor: filter.required
                                            ? 'default'
                                            : 'pointer',
                                        pt: 1,
                                        color: filter.required
                                            ? '#9198AA'
                                            : '#2B395B', // : '#9198AA',
                                        fontSize: '14px',
                                    }}
                                    onClick={() =>
                                        !filter.required && onRemove()
                                    }
                                >
                                    Remove filter
                                </Box>
                            </Box>
                        )}
                        <SubmitButton
                            variant="contained"
                            color="secondary"
                            sx={{ flexGrow: 1 }}
                            type="submit"
                            disabled={!formState.isDirty || !hasValue}
                        >
                            Add
                        </SubmitButton>
                    </Stack>
                )}
            </Stack>
        </Form>
    );
};
